import _ from "lodash";
import * as yup from "yup";
import { AURA_QUESTION, AURA_VALIDATION_MESSAGES } from "../config/const/enums";

function getMinMaxRangeUnitized(nextAuraQuestion) {
  let validationRanges =
    nextAuraQuestion?.constraints?.availableUnits[0]?.validationRanges;
  if (validationRanges) {
    let returnValidationRange = _.first(validationRanges);
    returnValidationRange.unitSymbol =
      nextAuraQuestion?.constraints?.availableUnits[0]?.label;
    return returnValidationRange;
  }
}

function getMnMaxRangeNumeric(nextAuraQuestion) {
  let returnValidationRange = {};
  returnValidationRange.lower = nextAuraQuestion?.constraints?.minVal;
  returnValidationRange.upper = nextAuraQuestion?.constraints?.maxVal;
  return returnValidationRange;
}

function getMinMaxRangeBP(nextAuraQuestion, bpType) {
  let returnBPValidationRange = {};
  if (bpType === AURA_QUESTION.AURA_BP_SYSTOLIC) {
    returnBPValidationRange.lower =
      nextAuraQuestion?.constraints?.minValSystolic;
    returnBPValidationRange.upper =
      nextAuraQuestion?.constraints?.maxValSystolic;
  } else if (bpType === AURA_QUESTION.AURA_BP_DIASTOLIC) {
    returnBPValidationRange.lower =
      nextAuraQuestion?.constraints?.minValDiastolic;
    returnBPValidationRange.upper =
      nextAuraQuestion?.constraints?.maxValDiastolic;
  }
  return returnBPValidationRange;
}

function getMinMaxDateRange(nextAuraQuestion) {
  let returnDateValidationRange = {};
  returnDateValidationRange.minDate = new Date(
    nextAuraQuestion?.constraints?.minDate
  );
  returnDateValidationRange.maxDate = new Date(
    nextAuraQuestion?.constraints?.maxDate
  );
  return returnDateValidationRange;
}

export function isDropDownNumericQuestion(nextAuraQuestion) {
  return nextAuraQuestion?.constraints?.availableUnits.length > 1
    ? true
    : false;
}

function getNumericDropdownSchema(nextAuraQuestion, unitizedDropdownOption) {
  if (unitizedDropdownOption) {
    const validationSelected = _.filter(
      nextAuraQuestion?.constraints?.availableUnits,
      ["unitSymbol", unitizedDropdownOption]
    );
    const validationRange = _.first(validationSelected).validationRanges[0];
    return yup.object().shape({
      unitizedValue: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .nullable()
        .min(
          validationRange.lower,
          `Please enter between ${validationRange.lower} and ${validationRange.upper}`
        )
        .max(
          validationRange.upper,
          `Please enter between ${validationRange.lower} and ${validationRange.upper}`
        )
        .required(AURA_VALIDATION_MESSAGES.FIELD_REQUIRED),
      unitizedOption: yup
        .string()
        .required(AURA_VALIDATION_MESSAGES.FIELD_REQUIRED),
    });
  } else {
    //when undefined for the first time
    return yup.object().shape({
      unitizedValue: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .nullable()
        .required(AURA_VALIDATION_MESSAGES.FIELD_REQUIRED),
      unitizedOption: yup
        .string()
        .required(AURA_VALIDATION_MESSAGES.FIELD_REQUIRED),
    });
  }
}

export function generateSchema(nextAuraQuestion, unitizedDropdownOption) {
  switch (nextAuraQuestion?.type) {
    case "SINGLE_CHOICE":
      return yup.object().shape({
        healthQuestion: yup.string().required(),
      });
    case "UNITIZED_CHOICE":
    case "DATE_CHOICE":
    case "NUMERIC_CHOICE":
      return yup.object().shape({
        healthQuestion: yup.string().required(),
      });
    case "UNITIZED":
      const dropDownNumericFlag = isDropDownNumericQuestion(nextAuraQuestion);
      if (dropDownNumericFlag) {
        return getNumericDropdownSchema(
          nextAuraQuestion,
          unitizedDropdownOption
        );
      } else {
        const unitizedRange = getMinMaxRangeUnitized(nextAuraQuestion);
        return yup.object().shape({
          healthQuestion: yup
            .number()
            .transform((value) => (isNaN(value) ? undefined : value))
            .nullable()
            .min(
              unitizedRange.lower,
              `Please enter between ${unitizedRange.lower} ${unitizedRange.unitSymbol} and ${unitizedRange.upper} ${unitizedRange.unitSymbol}`
            )
            .max(
              unitizedRange.upper,
              `Please enter between ${unitizedRange.lower} ${unitizedRange.unitSymbol} and ${unitizedRange.upper} ${unitizedRange.unitSymbol}`
            )
            .required(AURA_VALIDATION_MESSAGES.FIELD_REQUIRED),
        });
      }
    case "NUMERIC":
      const numericRange = getMnMaxRangeNumeric(nextAuraQuestion);
      return yup.object().shape({
        healthQuestion: yup
          .number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .nullable()
          .min(
            numericRange.lower,
            `Please enter between ${numericRange.lower} and ${numericRange.upper}`
          )
          .max(
            numericRange.upper,
            `Please enter between ${numericRange.lower} and ${numericRange.upper}`
          )
          .required(AURA_VALIDATION_MESSAGES.FIELD_REQUIRED),
      });

    case "MULTIPLE_CHOICE":
      return yup.object().shape({
        healthQuestion: yup
          .array()
          .nullable()
          .required("You must select at least one.")
          .test(
            "dontKnow-validation",
            "Can't select Don't know and any other value",
            function (value) {
              return value == null
                ? true
                : value.includes("ANSWER_IS_UNKNOWN_CODE") && value.length > 1
                  ? false
                  : true;
            }
          ),
      });
    case "TEXT":
      return yup.object().shape({
        healthQuestion: yup
          .string()
          .matches(
            /^[a-zA-Z0-9():;,.?' ]+$/,
            AURA_VALIDATION_MESSAGES.ALPHA_NUMERIC_FIELD
          )
          .required(),
      });
    case "SEARCH":
      return yup.object().shape({
        healthQuestion: yup
          .object()
          .nullable()
          .required("Please select an option."),
      });
    case "BLOOD_PRESSURE":
      const systolicRange = getMinMaxRangeBP(
        nextAuraQuestion,
        AURA_QUESTION.AURA_BP_SYSTOLIC
      );
      const diastolicRange = getMinMaxRangeBP(
        nextAuraQuestion,
        AURA_QUESTION.AURA_BP_DIASTOLIC
      );
      return yup.object().shape({
        systolicReading: yup
          .number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .nullable()
          .min(
            systolicRange.lower,
            `Please enter between ${systolicRange.lower} and ${systolicRange.upper}`
          )
          .max(
            systolicRange.upper,
            `Please enter between ${systolicRange.lower} and ${systolicRange.upper}`
          )
          .required(AURA_VALIDATION_MESSAGES.FIELD_REQUIRED),
        diastolicReading: yup
          .number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .nullable()
          .min(
            diastolicRange.lower,
            `Please enter between ${diastolicRange.lower} and ${diastolicRange.upper}`
          )
          .max(
            diastolicRange.upper,
            `Please enter between ${diastolicRange.lower} and ${diastolicRange.upper}`
          )
          .required(AURA_VALIDATION_MESSAGES.FIELD_REQUIRED),
      });
    case "DATE":
      const dateRangeValidation = getMinMaxDateRange(nextAuraQuestion);
      const dateMinMonthFormat = (dateRangeValidation.minDate.getMonth() + 1)
        .toString()
        .padStart(2, "0");
      const dateMaxMonthFormat = (dateRangeValidation.maxDate.getMonth() + 1)
        .toString()
        .padStart(2, "0");
      return yup.object().shape({
        healthQuestion: yup
          .date()
          .nullable()
          .min(
            dateRangeValidation.minDate,
            `Minimum date required is ${dateMinMonthFormat}/${dateRangeValidation.minDate.getFullYear()}`
          )
          .max(
            dateRangeValidation.maxDate,
            `Maximum date required is ${dateMaxMonthFormat}/${dateRangeValidation.maxDate.getFullYear()}`
          )
          .required(AURA_VALIDATION_MESSAGES.FIELD_REQUIRED)
          .typeError(AURA_VALIDATION_MESSAGES.VALID_DATE),
      });
    default:
      return yup.object().shape({
        // healthQuestion: yup.string().required(),
      });
  }
}
